import React, { useState, useRef, useEffect } from 'react';
import {
  UserCheck,
  FileCheck,
  Users,
  FileText,
  MoreHorizontal,
  Smartphone,
  CircleDollarSign,
  X
} from 'lucide-react';
import { playPulseSeal } from '../utils/pulseAudio';
import { UserSession, UserRole } from '../types';
import { MobileAccountRoleSection } from './MobileAccountRoleSection';

interface MobileSupervisorBottomNavProps {
  currentTab: string;
  onSelectTab: (tab: string) => void;
  user?: UserSession;
  onSwitchRole?: (role: UserRole) => void;
  onLogout?: () => void;
}

interface NavDestination {
  id: string;
  label: string;
  tab: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number; size?: number }>;
  isActive: (current: string) => boolean;
  activeColor: {
    text: string;
    icon: string;
    indicator: string;
    glow: string;
  };
}

export const MobileSupervisorBottomNav: React.FC<MobileSupervisorBottomNavProps> = ({
  currentTab,
  onSelectTab,
  user,
  onSwitchRole,
  onLogout
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreSheetRef = useRef<HTMLDivElement>(null);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleTouchOutside = (e: MouseEvent | TouchEvent) => {
      if (moreSheetRef.current && !moreSheetRef.current.contains(e.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    if (isMoreOpen) {
      document.addEventListener('mousedown', handleTouchOutside);
      document.addEventListener('touchstart', handleTouchOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleTouchOutside);
      document.removeEventListener('touchstart', handleTouchOutside);
    };
  }, [isMoreOpen]);

  const destinations: NavDestination[] = [
    {
      id: 'supervisor-nav-caseload',
      label: 'Caseload',
      tab: 'assignments',
      icon: UserCheck,
      isActive: (cur) => cur === 'assignments' || cur === 'scheduling' || cur === 'visit-planner',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'supervisor-nav-approval',
      label: 'Approval',
      tab: 'notes',
      icon: FileCheck,
      isActive: (cur) => cur === 'notes',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'supervisor-nav-clients',
      label: 'Clients',
      tab: 'clients',
      icon: Users,
      isActive: (cur) => cur === 'clients',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'supervisor-nav-documents',
      label: 'Documents',
      tab: 'clinical',
      icon: FileText,
      isActive: (cur) => cur === 'clinical',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'supervisor-nav-more',
      label: 'More',
      tab: 'more',
      icon: MoreHorizontal,
      isActive: (cur) => cur === 'mobile-soap' || cur === 'my-pay' || isMoreOpen,
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    }
  ];

  const handleTap = (tab: string) => {
    playPulseSeal({ cooldownMs: 120, volume: 0.35 });
    if (tab === 'more') {
      setIsMoreOpen(!isMoreOpen);
    } else {
      setIsMoreOpen(false);
      onSelectTab(tab);
    }
  };

  const handleSelectMoreOption = (tab: string) => {
    playPulseSeal({ cooldownMs: 120, volume: 0.35 });
    setIsMoreOpen(false);
    onSelectTab(tab);
  };

  return (
    <>
      {/* More Action Sheet Modal (Tactile Popover above Bottom Nav) */}
      {isMoreOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-xs md:hidden animate-in fade-in duration-150">
          <div
            ref={moreSheetRef}
            className="absolute bottom-[calc(52px+env(safe-area-inset-bottom,0px))] inset-x-3 bg-[#f8fafc] rounded-2xl border border-slate-200/90 shadow-[0_-8px_30px_rgba(15,23,42,0.12),0_4px_16px_rgba(15,23,42,0.08)] p-3.5 space-y-2.5 animate-in slide-in-from-bottom-3 duration-200"
          >
            <div className="relative flex items-center justify-between pb-2.5 border-b border-slate-200/90 px-1 min-h-[36px]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-4.5 rounded-full bg-slate-700 shrink-0" aria-hidden="true" />
                <div className="flex items-baseline gap-2 truncate">
                  <h2 className="text-sm font-bold tracking-tight text-slate-900 leading-none">
                    Supervisor Hub
                  </h2>
                  <span className="text-[10px] font-mono font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 leading-none shrink-0">
                    More
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsMoreOpen(false)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-200/90 transition-colors cursor-pointer shrink-0"
                aria-label="Close More Hub"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Account & Role Section */}
            {user && (
              <MobileAccountRoleSection
                user={user}
                onSwitchRole={onSwitchRole}
                onLogout={onLogout}
                onAfterAction={() => setIsMoreOpen(false)}
              />
            )}

            <div className="grid grid-cols-1 gap-2 pt-1">
              {/* Supervisor Clinical AI */}
              <button
                type="button"
                id="supervisor-more-ai"
                onClick={() => handleSelectMoreOption('assistant')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'assistant'
                    ? 'bg-slate-100 border border-slate-300 text-slate-950 shadow-xs'
                    : 'bg-white hover:bg-slate-50/80 border border-slate-200/90 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 shrink-0">
                    <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-5 h-5 shrink-0 object-contain" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Clinical AI</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                        Supervisor AI
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Co-signature rules, supervision audits & compliance
                    </div>
                  </div>
                </div>
              </button>

              {/* Mobile SOAP Option */}
              <button
                type="button"
                onClick={() => handleSelectMoreOption('mobile-soap')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'mobile-soap'
                    ? 'bg-slate-100 border border-slate-300 text-slate-950 shadow-xs'
                    : 'bg-white hover:bg-slate-50/80 border border-slate-200/90 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 shrink-0">
                    <Smartphone className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Mobile SOAP</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                        Direct Encounter
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Author authoritative direct treatment notes
                    </div>
                  </div>
                </div>
              </button>

              {/* My Pay Option */}
              <button
                type="button"
                onClick={() => handleSelectMoreOption('my-pay')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'my-pay'
                    ? 'bg-emerald-50/70 border border-emerald-300 text-slate-900 shadow-[0_1px_4px_rgba(16,185,129,0.08)]'
                    : 'bg-white hover:bg-slate-50/80 border border-slate-200/90 text-slate-800 shadow-[0_1px_3px_rgba(15,23,42,0.05)]'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shrink-0">
                    <CircleDollarSign className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>My Pay</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-emerald-100/70 text-emerald-700 border border-emerald-200 font-semibold">
                        Compensation
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Pay period units, rates, and earnings ledger
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor Fixed Bottom Navigation */}
      <nav
        id="pulse-mobile-supervisor-bottom-nav"
        aria-label="Supervisor Mobile Navigation"
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 border-t border-slate-200/90 shadow-[0_-2px_10px_rgba(15,23,42,0.06)] select-none backdrop-blur-md"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* 5 Primary Destination Grid */}
        <div className="grid grid-cols-5 h-[52px] items-stretch">
          {destinations.map((item) => {
            const active = item.isActive(currentTab);
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                id={item.id}
                type="button"
                onClick={() => handleTap(item.tab)}
                className="relative flex flex-col items-center justify-center h-full py-1 px-1 transition-all duration-150 active:scale-95 group focus:outline-none cursor-pointer"
                aria-current={active ? 'page' : undefined}
              >
                {/* Active top edge indicator bar */}
                {active && (
                  <div
                    className={`absolute top-0 h-[2.5px] w-7 rounded-full ${item.activeColor.indicator} transition-all duration-200`}
                  />
                )}

                {/* Active subtle pill background */}
                {active && (
                  <div className="absolute inset-x-1.5 top-1.5 bottom-1.5 bg-slate-200/50 rounded-xl pointer-events-none" />
                )}

                {/* Icon */}
                <div className="relative flex items-center justify-center shrink-0">
                  <Icon
                    className={`w-5 h-5 transition-all duration-150 ${
                      active
                        ? `${item.activeColor.icon} scale-105`
                        : 'text-slate-400 group-hover:text-slate-600'
                    }`}
                    strokeWidth={active ? 2.3 : 1.9}
                  />
                </div>

                {/* Destination Label - Single line guaranteed */}
                <span
                  className={`text-[9.5px] tracking-tight leading-none mt-1 whitespace-nowrap transition-colors select-none ${
                    active
                      ? `font-bold ${item.activeColor.text}`
                      : 'font-medium text-slate-500 group-hover:text-slate-700'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};
